"use client";

import Image from "next/image";
import { Tooltip as ReactTooltip } from "react-tooltip";
import "react-tooltip/dist/react-tooltip.css";

export default function ProjectConcept() {

    return (
        <div className="bg-bluebirdlightblue">
            <h1 className="text-4xl p-4 pl-12 mt-4">Business Modeling</h1>
            <p className="pl-12 pr-12 pb-4">
                BlueBird characterises the <strong>route to market</strong> of <strong>small-scale flexibilities</strong>. It requires a number of actors with different interests and a large number of data requirements and exchanges between the actors.
            </p>
            <p className="pl-12 pr-12 pb-4">
                In this respect, the <strong>barriers and drivers</strong> for the provision of small scale flexibilities are identified. The research focuses on flexibility market integration, identifying data flows 
                between market actors, elaborating baseline concepts and characterising barriers along the route to market for small flexibilities.
            </p>
            <div className="relative w-full max-w-4xl mx-auto">
                {/* Wrapper for Image and Hotspots */}
                <div className="relative w-full aspect-[21/16] flex items-center justify-center">
                    <div className="relative w-[90%] h-[90%]">

                    
                    {/* Main figure */}
                    <Image
                        src="/projectoverview/businessmodeling.png"
                        alt="Business Modeling"
                        fill
                        className="object-cover"
                    />

                    {/* --- Hotspot 1: Balancing --- */}
                    <div
                        data-tooltip-id="balancing-tooltip"
                        data-tooltip-html="The balancing market is often an economically <br />attractive market with relatively high entry <br />barriers. There are a number of requirements to <br />fulfill in order to participate in this market."
                        className="absolute top-[25%] left-[15%] w-[22.5%] h-[9%] /*bg-red-500/40 hover:bg-blue-500/40*/ cursor-pointer z-10"
                    />

                    {/* --- Hotspot 2: Day Ahead --- */}
                    <div
                        data-tooltip-id="dayahead-tooltip"
                        data-tooltip-html="The day-ahead market produces benchmark results <br />on the value of electricity across different time scales."
                        className="absolute top-[25%] left-[39%] w-[22.5%] h-[9%] /*bg-red-500/40 hover:bg-blue-500/40*/ cursor-pointer z-10"
                    />

                    {/* --- Hotspot 3: Intraday --- */}
                    <div
                        data-tooltip-id="intraday-tooltip"
                        data-tooltip-html="The intraday market allows for short-term trading <br />of electricity and is thus a correction of the <br />day-ahead market results. The market is <br />characterised by high volatility and is partly <br />dominated by algorithmic trading."
                        className="absolute top-[25%] left-[63%] w-[22.5%] h-[9%] /*bg-red-500/40 hover:bg-blue-500/40*/ cursor-pointer z-10"
                    />

                    {/* --- Hotspot 4: Supplier --- */}
                    <div
                        data-tooltip-id="supplier-tooltip"
                        data-tooltip-html="The supplier acquires electricity on the <br />wholesale market, delivers it to the end consumer<br /> and is responsible for potential imbalances."
                        className="absolute top-[38%] left-[9%] w-[32%] h-[12%] /*bg-red-500/40 hover:bg-blue-500/40*/ cursor-pointer z-10"
                    />

                    {/* --- Hotspot 5: DSO --- */}
                    <div
                        data-tooltip-id="dso-tooltip"
                        data-tooltip-html="The DSO connects most of the small scale flexibilities<br /> as well as end consumers. In addition, secure network<br /> operation needs to be maintained through accurate planning."
                        className="absolute top-[59.5%] left-[9%] w-[32%] h-[12%] /*bg-red-500/40 hover:bg-blue-500/40*/ cursor-pointer z-10"
                    />

                    {/* --- Hotspot 6: Flexibility Service Provider --- */}
                    <div
                        data-tooltip-id="flexibility-service-provider-tooltip"
                        data-tooltip-html="The flexibility service provider is offering flexibility<br /> to the market and tries to maximise ist economic value."
                        className="absolute top-[38%] left-[59%] w-[32%] h-[12%] /*bg-red-500/40 hover:bg-blue-500/40*/ cursor-pointer z-10"
                    />

                    {/* --- Hotspot 7: Technical Aggregator --- */}
                    <div
                        data-tooltip-id="technical-aggregator-tooltip"
                        data-tooltip-html="The technical aggregator connects the household flexibility, <br />develops projections on the flexibility potential and is <br />responsible for actual controlling of the relevant components."
                        className="absolute top-[59.5%] left-[59%] w-[32%] h-[12%] /*bg-red-500/40 hover:bg-blue-500/40*/ cursor-pointer z-10"
                    />
                    </div>
                </div>

                {/* Tooltips */}
                <ReactTooltip id="balancing-tooltip" place="top" className="!bg-bluebirdyellow !text-black !rounded-md !opacity-100" />
                <ReactTooltip id="dayahead-tooltip" place="top" className="!bg-bluebirdyellow !text-black !rounded-md !opacity-100" />
                <ReactTooltip id="intraday-tooltip" place="top" className="!bg-bluebirdyellow !text-black !rounded-md !opacity-100" />
                <ReactTooltip id="supplier-tooltip" place="top" className="!bg-bluebirdyellow !text-black !rounded-md !opacity-100" />
                <ReactTooltip id="dso-tooltip" place="bottom" className="!bg-bluebirdyellow !text-black !rounded-md !opacity-100" />
                <ReactTooltip id="flexibility-service-provider-tooltip" place="top" className="!bg-bluebirdyellow !text-black !rounded-md !opacity-100" />
                <ReactTooltip id="technical-aggregator-tooltip" place="bottom" className="!bg-bluebirdyellow !text-black !rounded-md !opacity-100" />

                <p className="text-right text-sm mb-4 italic">
                    💡 Hover over the different components for more information
                </p>
            </div>
        </div>
    )
}